import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Users, CalendarDays, ClipboardList, Wallet, Boxes,
  Target, BellRing, FileBarChart, Sparkles, Settings as SettingsIcon,
  Sun, Moon, LogOut, Search as SearchIcon, Home, Compass, Hash, Heart, Menu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/consultations", label: "Consultations", icon: ClipboardList, testid: "nav-consultations" },
  { to: "/appointments", label: "Appointments", icon: CalendarDays, testid: "nav-appointments" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
  { to: "/products", label: "Products", icon: Boxes, testid: "nav-products" },
  { to: "/leads", label: "Leads", icon: Target, testid: "nav-leads" },
  { to: "/followups", label: "Follow-ups", icon: BellRing, testid: "nav-followups" },
  { to: "/kundli", label: "Kundli", icon: Compass, testid: "nav-kundli" },
  { to: "/kundli-milan", label: "Kundli Milan", icon: Heart, testid: "nav-milan" },
  { to: "/vastu", label: "Vastu", icon: Home, testid: "nav-vastu" },
  { to: "/numerology", label: "Numerology", icon: Hash, testid: "nav-numerology" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const SidebarInner = ({ user, onNavigate, idSuffix = "" }) => {
  const initials = (user?.name || "?").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex flex-col h-full bg-[#0B1120] text-slate-200">
      <div className="px-6 py-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-slate-900 font-bold shadow-lg">
            <Compass className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-lg text-white leading-tight">Trinandan</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80">Astrology CRM</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            onClick={onNavigate}
            data-testid={`${n.testid}${idSuffix}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all group ${
                isActive
                  ? "bg-gradient-to-r from-amber-500/20 to-transparent text-amber-300 border border-amber-500/30"
                  : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
            <span className="font-medium">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Signed in as</div>
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9 border border-amber-500/40">
            <AvatarFallback className="bg-slate-800 text-amber-400 text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{(user?.role || "").replace("_", " ")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const initials = (user?.name || "?").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen mandala-bg" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 border-r border-slate-800 z-30 hidden lg:flex flex-col" data-testid="sidebar">
        <SidebarInner user={user} />
      </aside>

      {/* Mobile drawer sidebar */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="p-0 w-72 lg:hidden bg-[#0B1120] border-r-slate-800" data-testid="mobile-nav-drawer">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation menu</SheetTitle>
          </SheetHeader>
          <SidebarInner user={user} onNavigate={() => setMobileNavOpen(false)} idSuffix="-mobile" />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-[#0B1120]/85 border-b border-slate-200 dark:border-slate-800" data-testid="topbar">
          <div className="px-3 sm:px-4 md:px-8 h-16 flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost" size="icon" onClick={() => setMobileNavOpen(true)}
              className="lg:hidden rounded-full hover:bg-amber-500/10 hover:text-amber-500 shrink-0"
              data-testid="mobile-nav-toggle" aria-label="Open menu"
            >
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            </Button>

            <button
              onClick={() => setSearchOpen(true)}
              data-testid="global-search-trigger"
              className="flex-1 max-w-xl flex items-center gap-2 h-10 sm:h-11 px-3 sm:px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 hover:border-amber-400/60 transition-colors text-left text-sm text-slate-500 dark:text-slate-400"
            >
              <SearchIcon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="truncate hidden sm:inline">Search clients, consultations, leads…</span>
              <span className="truncate sm:hidden">Search…</span>
              <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 hidden sm:inline">⌘K</kbd>
            </button>

            <Button
              variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle"
              className="rounded-full hover:bg-amber-500/10 hover:text-amber-500 shrink-0"
            >
              {theme === "dark" ? <Sun className="w-[18px] h-[18px]" strokeWidth={1.5} /> : <Moon className="w-[18px] h-[18px]" strokeWidth={1.5} />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full h-10 pl-1 pr-1 sm:pr-3 gap-2 shrink-0" data-testid="profile-menu">
                  <Avatar className="w-8 h-8 border border-amber-500/40">
                    <AvatarFallback className="bg-slate-100 dark:bg-slate-800 text-amber-600 dark:text-amber-400 text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/login"); }}
                  className="text-red-600 focus:text-red-600"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-3 sm:p-4 md:p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>

      {/* Global search sheet */}
      <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
        <SheetContent side="top" className="max-w-3xl mx-auto rounded-b-2xl" data-testid="global-search-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">Search</SheetTitle>
          </SheetHeader>
          <GlobalSearch onNavigate={() => setSearchOpen(false)} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
